import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getCurrentUserSession, signIn, signOut, signUp } from "./CognitoClient";

interface AuthValue {
  loading: boolean;
  email: string | null;
  token: string | null;
  signIn: (email: string, password: string, remember: boolean) => Promise<void>;
  signOut: () => void;
  signUp: (email: string, password: string, name: string) => Promise<void>;
}

const Ctx = createContext<AuthValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const session = await getCurrentUserSession();
    if (!session) {
      setEmail(null);
      setToken(null);
    } else {
      setToken(session.getIdToken().getJwtToken());
      const payload = session.getIdToken().payload as { email?: string };
      setEmail(payload.email ?? null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Silently refresh near expiry — Cognito SDK auto-refreshes on getSession()
  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => void refresh(), 45 * 60 * 1000); // every 45 min
    return () => clearInterval(id);
  }, [token, refresh]);

  const value = useMemo<AuthValue>(
    () => ({
      loading,
      email,
      token,
      signIn: async (e, p, r) => {
        await signIn(e, p, r);
        await refresh();
      },
      signOut: () => {
        signOut();
        setEmail(null);
        setToken(null);
      },
      signUp: async (e, p, n) => {
        await signUp(e, p, n);
      },
    }),
    [loading, email, token, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAuth = (): AuthValue => {
  const v = useContext(Ctx);
  if (!v) throw new Error("AuthProvider missing");
  return v;
};
